import { createMachine } from 'xstate';

import shared from 'common/bento/shared';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import { AnyMachineEvent } from 'features/Bento/types/Abstract';
import locales from 'features/Onboarding/locales';
import { ApplicationType } from 'features/Onboarding/types';

import actions from './actions';
import guards, { Guard } from './guards';
import model, { Context } from './model';
import services, { Service } from './services';
import { State } from './state';

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'personal-info',

    initial: State.INIT,

    on: {
      '*': {
        actions: shared.actions.unhandled(),
      },
    },

    states: {
      [State.INIT]: {
        always: [
          {
            cond: Guard.HasProfileFilled,
            target: State.BUSINESS_ROLE,
          },
          {
            cond: Guard.HasSupposedDirectors,
            target: State.SELECT_YOURSELF,
          },
          {
            target: State.NAME,
          },
        ],
      },

      [State.SELECT_YOURSELF]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: [
            {
              cond: Guard.CanSeeResetPrompt,
              target: State.ABORT_TO_RESET_PROMPT,
            },
            {
              actions: shared.actions.toast({
                status: 'error',
                title: locales.toast.definitiveStep,
              }),
            },
          ],
          SUBMIT_DIRECTOR_SELECTION: State.SEND_OFFICER_DATA,
          SUBMIT_NON_OFFICER: State.ONLY_CORPORATE_OFFICER,
          SUBMIT_NOT_FOUND: State.NAME,
        },
      },

      [State.SEND_OFFICER_DATA]: {
        invoke: {
          onDone: [
            {
              cond: Guard.HasProfileFilled,
              target: State.BUSINESS_ROLE,
            },
            { target: State.NAME },
          ],
          /**
           * On error, we log the error
           * and continue as if we didn't find public directors at all.
           * We do not log the error if it occurs because of a missing gender.
           */
          onError: [
            {
              cond: Guard.IsDirectorMissingGenderError,
              target: State.NAME,
            },
            {
              actions: shared.actions.error(),
              target: State.NAME,
            },
          ],
          src: Service.SaveDirectorSelection,
        },
      },

      [State.NAME]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: [
            {
              cond: {
                type: Guard.IsApplicationType,
                value: ApplicationType.CapitalDeposit,
              },
              target: State.ABORT,
            },
            {
              cond: {
                type: Guard.IsApplicationType,
                value: ApplicationType.ShineStart,
              },
              target: State.ABORT,
            },
            {
              cond: Guard.CanSeeResetPrompt,
              target: State.ABORT_TO_RESET_PROMPT,
            },
            {
              actions: shared.actions.toast({
                status: 'error',
                title: locales.toast.definitiveStep,
              }),
              cond: Guard.HasProfileFilled,
            },
            {
              cond: Guard.HasSupposedDirectors,
              target: State.SELECT_YOURSELF,
            },
            {
              actions: shared.actions.toast({
                status: 'error',
                title: locales.toast.definitiveStep,
              }),
            },
          ],
          SUBMIT_PERSONAL_NAME: State.SEND_NAME,
        },
      },

      [State.SEND_NAME]: {
        invoke: {
          onDone: {
            target: State.BUSINESS_ROLE,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.NAME,
          },
          src: Service.SendName,
        },
      },

      [State.BUSINESS_ROLE]: {
        always: {
          cond: Guard.IsEi,
          target: State.PERSONAL_ADDRESS,
        },

        entry: shared.actions.trackView(),

        on: {
          PREV: State.NAME,
          SUBMIT_BUSINESS_ROLE: State.SEND_BUSINESS_ROLE,
        },
      },

      [State.SEND_BUSINESS_ROLE]: {
        invoke: {
          onDone: [
            {
              cond: Guard.IsRoleIneligible,
              target: State.ONLY_CORPORATE_OFFICER,
            },
            {
              target: State.PERSONAL_ADDRESS,
            },
          ],
          onError: {
            actions: shared.actions.error(),
            target: State.BUSINESS_ROLE,
          },
          src: Service.SaveBusinessRole,
        },
      },

      [State.ONLY_CORPORATE_OFFICER]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.BUSINESS_ROLE,
        },
      },

      [State.PERSONAL_ADDRESS]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: State.COMPLIANCE,
          PREV: [
            {
              cond: Guard.IsEi,
              target: State.NAME,
            },
            { target: State.BUSINESS_ROLE },
          ],
        },
      },

      [State.COMPLIANCE]: {
        on: {
          NEXT: State.DONE,
          PREV: State.PERSONAL_ADDRESS,
        },
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },

      [State.ABORT_TO_RESET_PROMPT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },

        type: 'final',
      },
    },
  },

  {
    actions,
    guards,
    services,
  },
);

export default machine;
